import { Container, Sprite, TilingSprite, Texture } from 'pixi.js';
import { AREA_HEIGHT, AREA_WIDTH, getTile, KnyttArea } from '../engine/KnyttArea';
import { Tileset, TILE_SIZE } from './Tileset';

export const VIEW_W = AREA_WIDTH * TILE_SIZE; // 600
export const VIEW_H = AREA_HEIGHT * TILE_SIZE; // 240

/**
 * Renders a single area: a tiling gradient background plus four tile layers.
 * Layer containers are exposed so the game can interleave objects / the player
 * at the correct z-order (objects between layers, Juni above layer 3).
 */
export class AreaView {
  readonly root = new Container();
  readonly background: TilingSprite;
  /** The four tile-layer containers (index 0..3). */
  readonly tileLayers: Container[] = [];
  /**
   * Set when the area's `Overlay=true` extra-data moves tile layer 2 above
   * objects (GDAreaTiles.cs: `Layers[2].ZIndex = 12`). The caller must add
   * this directly to the world layer (not `root`) at that z-index.
   */
  readonly overlayLayer: Container | null = null;

  constructor(
    readonly area: KnyttArea,
    tilesetA: Tileset | null,
    tilesetB: Tileset | null,
    gradient: Texture | null,
  ) {
    this.root.x = area.position.x * VIEW_W;
    this.root.y = area.position.y * VIEW_H;

    this.background = new TilingSprite({
      texture: gradient ?? Texture.WHITE,
      width: VIEW_W,
      height: VIEW_H,
    });
    if (!gradient) this.background.tint = 0x000000;
    this.root.addChild(this.background);

    const overlay = area.getExtraData('Overlay')?.toLowerCase() === 'true';
    for (let layer = 0; layer < 4; layer++) {
      const c = new Container();
      this.tileLayers.push(c);
      if (layer === 2 && overlay) this.overlayLayer = c;
      else this.root.addChild(c);
      if (!area.empty) this.buildLayer(c, layer, tilesetA, tilesetB);
    }
  }

  private buildLayer(
    container: Container,
    layer: number,
    tilesetA: Tileset | null,
    tilesetB: Tileset | null,
  ): void {
    const data = this.area.tileLayers[layer];
    if (!data) return;
    for (let y = 0; y < AREA_HEIGHT; y++) {
      for (let x = 0; x < AREA_WIDTH; x++) {
        const b = getTile(data, x, y);
        if (b === 0 || b === 128) continue;
        let ts: Tileset | null;
        let index: number;
        if (b < 128) {
          ts = tilesetB;
          index = b;
        } else {
          ts = tilesetA;
          index = b - 128;
        }
        if (!ts) continue;
        const frame = ts.frames[index];
        if (!frame) continue;
        const sp = new Sprite(frame);
        sp.x = x * TILE_SIZE;
        sp.y = y * TILE_SIZE;
        container.addChild(sp);
      }
    }
  }

  /** Hide the tile sprite at (tx,ty) in a layer (NoWall moves it to ghost terrain). */
  hideTile(layer: number, tx: number, ty: number): void {
    const c = this.tileLayers[layer];
    if (!c) return;
    const px = tx * TILE_SIZE;
    const py = ty * TILE_SIZE;
    for (const child of c.children) {
      if (child.x === px && child.y === py) child.visible = false;
    }
  }

  destroy(): void {
    this.overlayLayer?.destroy({ children: true });
    this.root.destroy({ children: true });
  }
}
